import {type FC, memo, useCallback} from 'react';
import {Link} from 'react-router';

import classNames from 'classnames';

import type {
  parseDuration,
  parsePaymentPlan,
} from '@yourcoach/shared/utils/entity/program';

import {useI18n} from '@yourcoach/i18n';
import InfoIcon from '@yourcoach/shared/assets/icons/info.svg';
import MoreIcon from '@yourcoach/shared/assets/icons/more.svg';
import {Image} from '@yourcoach/shared/uikit/Image';
import {getFileSrc} from '@yourcoach/shared/utils/entity/file';
import {
  FULL_PORTION,
  getAverageMonthlyPayment,
  getMonthlyPaymentPerUnitString,
  ProgramPaymentTypeEnum,
} from '@yourcoach/shared/utils/entity/program';
import {extractCommission, getPriceString} from '@yourcoach/shared/utils/money';

import AvatarPlaceholder from '@src/components/AvatarPlaceholder';
import Button from '@src/components/Button';
import TextField from '@src/components/TextField';

import type {Coach} from './CocoachingManager';

import styles from './CocoachingManagerListItem.module.css';

type Props = {
  cantAcceptPayments: boolean;
  className?: string;
  coach: Coach;
  onMoreButtonClick?: (
    item: Coach,
    event: React.MouseEvent<Element, MouseEvent>,
  ) => void;
  onPriceChange?: (price: string, coach: Coach) => void;
  parsedDuration: ReturnType<typeof parseDuration>;
  parsedPaymentPlan: ReturnType<typeof parsePaymentPlan>;
  priceIsEditable: boolean;
  role: 'cocoach' | 'owner' | 'you';
  showMoreButton?: boolean;
  status?: 'approved' | 'pending' | 'rejected';
};

const CocoachingManagerListItem: FC<Props> = ({
  cantAcceptPayments,
  className,
  coach,
  onMoreButtonClick,
  onPriceChange,
  parsedDuration,
  parsedPaymentPlan,
  priceIsEditable,
  role,
  showMoreButton,
  status,
}) => {
  const {t} = useI18n();
  const onPriceChangeCb: React.ChangeEventHandler<HTMLInputElement> =
    useCallback(
      e => {
        onPriceChange?.(e.currentTarget.value, coach);
      },
      [coach, onPriceChange],
    );

  const onMoreButtonClickCb: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      e.preventDefault();

      onMoreButtonClick?.(coach, e);
    },
    [coach, onMoreButtonClick],
  );

  const {parsedCurrency, paymentType, processing, total} = parsedPaymentPlan;

  const coachPrice = Math.trunc(total * (coach.portion / FULL_PORTION));

  const coachCommission = Math.trunc(
    extractCommission(total) * (coach.portion / FULL_PORTION),
  );

  const monthlyPayment = getAverageMonthlyPayment(
    coachPrice,
    parsedDuration.num,
  );

  return (
    <div className={classNames(styles.container, className)}>
      <div className={styles.coachContainer}>
        <div className={styles.avatarContainer}>
          <Image
            className={styles.avatar}
            placeholder={<AvatarPlaceholder name={coach.name || ''} />}
            src={getFileSrc(coach.avatar, 250).url}
          />
        </div>
        <div className={styles.nameContainer}>
          <Link
            className={styles.name}
            target="_blank"
            to={`/coaches/${coach.slug || coach._id}`}
          >
            {coach.name}
          </Link>
          <div className={styles.roleContainer}>
            <p className={styles.role}>
              {
                /**
                 * @i18n_parser
                 *
                 * t('shared:CocoachingManager.role.cocoach')
                 * t('shared:CocoachingManager.role.owner')
                 * t('shared:CocoachingManager.role.you')
                 */
                t(`shared:CocoachingManager.role.${role}`)
              }
            </p>
            {status ? (
              <p className={classNames(styles.status, styles[status])}>
                {
                  /**
                   * @i18n_parser
                   *
                   * t('shared:CocoachingManager.status.approved')
                   * t('shared:CocoachingManager.status.pending')
                   * t('shared:CocoachingManager.status.rejected')
                   */
                  t(`shared:CocoachingManager.status.${status}`)
                }
              </p>
            ) : null}
          </div>
          {cantAcceptPayments ? (
            <p className={styles.alert}>
              <InfoIcon />
              {t('shared:CocoachingManager.coach_isnt_provider_label')}
            </p>
          ) : null}
        </div>
      </div>
      <div
        className={classNames(
          styles.rightPart,
          showMoreButton && styles.withMoreButton,
        )}
      >
        {onPriceChange ? (
          <div className={styles.portionContainer}>
            <TextField
              className={styles.portionInput}
              disabled={!priceIsEditable}
              onChange={onPriceChangeCb}
              value={coach.price || ''}
            />
            {coach.portion && processing === 'internal' ? (
              <>
                <p className={styles.price}>
                  {t('shared:CocoachingManager.commission_label', {
                    commission:
                      paymentType.id === ProgramPaymentTypeEnum.MONTHLY.id
                        ? `${getPriceString({
                            currency: parsedCurrency,
                            price: extractCommission(monthlyPayment),
                          })}${getMonthlyPaymentPerUnitString().toLowerCase()}`
                        : getPriceString({
                            price: coachCommission,
                          }),
                  })}
                </p>
                <p className={styles.price}>
                  {t('shared:CocoachingManager.total_label', {
                    price:
                      paymentType.id === ProgramPaymentTypeEnum.MONTHLY.id
                        ? `${getPriceString({
                            currency: parsedCurrency,
                            price:
                              monthlyPayment -
                              extractCommission(monthlyPayment),
                          })}${getMonthlyPaymentPerUnitString().toLowerCase()}`
                        : getPriceString({
                            currency: parsedCurrency,
                            price: coachPrice - coachCommission,
                          }),
                  })}
                </p>
              </>
            ) : null}
            {coach.portion &&
            processing === 'external' &&
            paymentType.id === ProgramPaymentTypeEnum.MONTHLY.id ? (
              <p className={styles.price}>
                {`${getPriceString({
                  currency: parsedCurrency,
                  price: monthlyPayment,
                })}${getMonthlyPaymentPerUnitString().toLowerCase()}`}
              </p>
            ) : null}
          </div>
        ) : null}
        {showMoreButton ? (
          onMoreButtonClick ? (
            <Button className={styles.moreButton} onClick={onMoreButtonClickCb}>
              <MoreIcon />
            </Button>
          ) : (
            <div className={classNames(styles.moreButton, styles.stub)}>
              <MoreIcon />
            </div>
          )
        ) : null}
      </div>
    </div>
  );
};

export default memo(CocoachingManagerListItem);
